import mapboxgl from 'mapbox-gl'

export const USA_CENTERED_BOUNDS = [
  [-125, 24],   // Southwest
  [-66.5, 49],  // Northeast
] as mapboxgl.LngLatBoundsLike

export const initializeFilteredPatternToMap = (map: mapboxgl.Map) => {
  const patternCanvas = document.createElement('canvas')
  const patternContext = patternCanvas.getContext('2d')
  if (patternContext) {
    patternCanvas.width = 8
    patternCanvas.height = 8
    patternContext.fillStyle = '#FFFFFF'
    patternContext.fillRect(0, 0, 8, 8)
    patternContext.strokeStyle = 'rgba(0, 0, 0, 0.3)'
    patternContext.lineWidth = 1
    patternContext.beginPath()
    patternContext.moveTo(0, 0)
    patternContext.lineTo(8, 8)
    patternContext.stroke()

    // Convert canvas to ImageData
    const imageData = patternContext.getImageData(0, 0, 8, 8)
    map.addImage('diagonal-stripes', imageData)
  }
}

export const setStateLayerOpacities = (map: mapboxgl.Map) => {
  map.addLayer({
    id: 'state-fills',
    type: 'fill',
    source: 'states',
    paint: {
      'fill-opacity': [
        'case',
        ['boolean', ['feature-state', 'hover'], false],
        1.0,
        0.75,
      ],
    },
  })
}

export const applyFilteredPatternLayersToFilteredStates = (map: mapboxgl.Map, filteredStates: string[]) => {
  map.addLayer({
    id: 'state-fills-pattern',
    type: 'fill',
    source: 'states',
    paint: {
      'fill-pattern': 'diagonal-stripes',
      'fill-opacity': [
        'case',
        ['in', ['get', 'STATE_NAME'], ['literal', filteredStates]],
        0.5,
        0
      ],
    },
  })
}

export const applyBorderLayersToStates = (map: mapboxgl.Map, filteredStates: string[]) => {
  map.addLayer({
    id: 'state-borders',
    type: 'line',
    source: 'states',
    paint: {
      'line-color': [
        'case',
        ['in', ['get', 'STATE_NAME'], ['literal', filteredStates]],
        '#000000',
        '#627BC1'
      ],
      'line-width': [
        'case',
        ['in', ['get', 'STATE_NAME'], ['literal', filteredStates]],
        3,
        2
      ],
    },
  })
}

export const initializeStateClickHandler = (map: mapboxgl.Map, {
  isLoading,
  cumulativeAllowanceByState,
  onStateClick
}: {
  isLoading: boolean,
  cumulativeAllowanceByState: Record<string, number>,
  onStateClick: (state: string) => void
}) => {
  const clickHandler = (e: mapboxgl.MapMouseEvent & { features?: mapboxgl.MapboxGeoJSONFeature[] }) => {
    if (e.features && e.features.length > 0 && e.features[0].properties) {
      const stateName = e.features[0].properties.STATE_NAME
      if (!isLoading && cumulativeAllowanceByState[stateName] !== undefined) {
        onStateClick(stateName)
      }
    }
  }
  map.off('click', 'state-fills', clickHandler)
  map.on('click', 'state-fills', clickHandler)
}

export const fetchAndInitializeMapData = (
  map: mapboxgl.Map,
  { filteredStates, getColorForState }:
  { filteredStates: string[], getColorForState: (stateName: string) => string }
) => {
  // TODO: add eslint-disable-next-line for bare fetch
  fetch('https://docs.mapbox.com/mapbox-gl-js/assets/us_states.geojson')
    .then(response => response.json())
    .then(statesData => {
      const source = map.getSource('states') as mapboxgl.GeoJSONSource
      if (source) {
        // Add colors to the feature properties
        const features = statesData.features.map((feature: any) => {
          const stateName = feature.properties.STATE_NAME
          const color = getColorForState(stateName)

          return {
            ...feature,
            properties: {
              ...feature.properties,
              fillColor: color
            }
          }
        })

        source.setData({
          type: 'FeatureCollection',
          features
        })

        // Set the fill color to use the property
        map.setPaintProperty('state-fills', 'fill-color', [
          'get',
          'fillColor'
        ])

        // Update pattern visibility based on filtered states
        map.setPaintProperty('state-fills-pattern', 'fill-opacity', [
          'case',
          ['in', ['get', 'STATE_NAME'], ['literal', filteredStates]],
          0.5,
          0
        ])

        map.setPaintProperty('state-borders', 'line-color', 'white')

        map.setPaintProperty('state-borders', 'line-width', [
          'case',
          ['in', ['get', 'STATE_NAME'], ['literal', filteredStates]],
          2,
          1
        ])
      }
    })
}

export const initializeMapbox = (container: HTMLDivElement, onLoad: () => void) => {
  const map = new mapboxgl.Map({
    container,
    style: 'mapbox://styles/mapbox/light-v11',
    center: [-98.5, 39.8],
    zoom: 20,
    dragPan: true,
    scrollZoom: true,
    doubleClickZoom: false,
    touchZoomRotate: false,
    dragRotate: false,
    attributionControl: false,
    logoPosition: undefined,
    trackResize: true,
  })
  map.getCanvas().style.cursor = 'default'
  map.on('load', () => {
    onLoad()
    const layers = map.getStyle().layers
    map.setPaintProperty('water', 'fill-color', '#A3D3F5')
    layers?.forEach(layer => {
      if (layer.type === 'symbol' &&
          (layer.id.includes('label') || layer.id.includes('place') || layer.id.includes('text'))) {
        map.setLayoutProperty(layer.id, 'visibility', 'none')
      }
    })
  })
  return map
}

export const setColorsForStateLayers = (map: mapboxgl.Map, filteredStates: string[], getColorForState: (stateName: string) => string) => {
  const source = map.getSource('states') as mapboxgl.GeoJSONSource
  if (!source) return
  // @ts-expect-error Accessing private _data to get current GeoJSON features for bounding box
  const data = source._data
  if (!data?.features) return
  const features = data.features.map((feature: any) => {
    const stateName = feature.properties.STATE_NAME
    const color = getColorForState(stateName)
    return {
      ...feature,
      properties: {
        ...feature.properties,
        fillColor: color
      }
    }
  })
  source.setData({
    type: 'FeatureCollection',
    features
  })
  map.setPaintProperty('state-fills-pattern', 'fill-opacity', [
    'case',
    ['in', ['get', 'STATE_NAME'], ['literal', filteredStates]],
    0.5,
    0
  ])
  map.setPaintProperty('state-borders', 'line-color', 'white')
  map.setPaintProperty('state-borders', 'line-width', [
    'case',
    ['in', ['get', 'STATE_NAME'], ['literal', filteredStates]],
    2,
    1
  ])
}
